/**
 * KRA Photo Journal – Bulk Sort Panel
 *
 * Admin-only panel for triaging unsorted Drive folders into kimono-type
 * category folders inside `01. Kimono`. Each unsorted subfolder is shown
 * as a card with a preview thumbnail of its first image; admin renames
 * it and picks the target category; the folder is moved in Drive via
 * the user's OAuth token, then an Airtable record is created via the
 * existing `/api/bulk-import` endpoint.
 *
 * Parent "01. Kimono" folder ID is configured once and cached in
 * localStorage (see lib/sort-cache.ts). The target-category list is
 * cached until the admin clicks Refresh.
 *
 * Source folders (unsorted items) are discovered by name under the parent:
 *   "01. Chưa phân loại" — the one inbox — plus the legacy
 *   "01. Mới tải xuống 2" while it still exists (anh is merging it in).
 *   Each card remembers which inbox it came from for the Drive move.
 *
 * Excluded from the target dropdown:
 *   - The inbox folders themselves
 *   - "Trang" (per admin preference)
 */

import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  useGoogleDrive,
  listSubfolders,
  moveFolder,
  trashFolder,
  firstImageThumbnail,
  extractFolderIdFromUrl,
  folderUrl,
} from "../hooks/useGoogleDrive.js";
import {
  getSortParent,
  setSortParent,
  clearSortParent,
  getSortTargets,
  setSortTargets,
  clearSortTargets,
  type SortTarget,
} from "../lib/sort-cache.js";
import { suggestFolderTags } from "@kra/photo-journal-shared";
import { bulkImport, bulkScan } from "../lib/api.js";

interface BulkSortPanelProps {
  onClose: () => void;
}

const SOURCE_NAME = "01. Chưa phân loại";
/** Old inbox, still scanned until it is emptied into SOURCE_NAME and deleted. */
const LEGACY_SOURCE_NAMES = ["01. Mới tải xuống 2"];
const SOURCE_NAMES = new Set([SOURCE_NAME, ...LEGACY_SOURCE_NAMES]);
const EXCLUDED_TARGET_NAMES = new Set([...SOURCE_NAMES, "Trang"]);

interface UnsortedItem {
  id: string;
  name: string;
  /** Inbox folder it sits in now — Drive needs the old parent to move it. */
  sourceId: string;
  /** Still in the legacy "01. Mới tải xuống 2" inbox. */
  fromLegacy: boolean;
  /** Edited name (defaults to original). Used on submit to rename in Drive. */
  renameTo: string;
  /** Chosen target category ID, or null if not yet chosen. */
  targetId: string | null;
  /** Preview thumbnail URL, or null while loading / if folder is empty. */
  thumbnail: string | null;
  thumbLoaded: boolean;
  /** Submission state */
  status: "idle" | "moving" | "done" | "deleting" | "deleted" | "error";
  error?: string;
}

export function BulkSortPanel({ onClose }: BulkSortPanelProps) {
  const { accessToken, requestToken } = useGoogleDrive();

  const [parentId, setParentIdState] = useState<string | null>(() => getSortParent());
  const [parentInput, setParentInput] = useState("");
  const [targets, setTargets] = useState<SortTarget[] | null>(() => getSortTargets());
  const [items, setItems] = useState<UnsortedItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Auth ────────────────────────────────────────────────────────────────
  const ensureToken = useCallback(async (): Promise<string | null> => {
    if (accessToken) return accessToken;
    try {
      return await requestToken();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Đăng nhập Google thất bại");
      return null;
    }
  }, [accessToken, requestToken]);

  // ── Load target list + source ID from the parent ──────────────────────
  const loadFromParent = useCallback(
    async (token: string, id: string, force: boolean) => {
      setLoading(true);
      setError(null);
      try {
        // Bulk-scan resolves target names → Airtable kimono_tag via the
        // server's KIMONO_TAG_MAP, so the panel doesn't have to duplicate
        // that logic. It also scans 2 levels which is a bit of extra work,
        // but cheap enough and only happens on first load + manual refresh.
        let freshTargets: SortTarget[] | null = force ? null : getSortTargets();

        if (!freshTargets) {
          const scan = await bulkScan(id);
          freshTargets = scan.categories
            .filter((c) => !EXCLUDED_TARGET_NAMES.has(c.name.trim()))
            .map((c) => ({
              id: c.folderId,
              name: c.name,
              suggestedTag: c.suggestedTag,
            }));
          setSortTargets(freshTargets);
          setTargets(freshTargets);
        }

        // Inbox folders — always fetched fresh (cheap single listing).
        const children = await listSubfolders(id, token);
        const sources = children.filter((f) => SOURCE_NAMES.has(f.name.trim()));

        if (sources.length === 0) {
          setError(
            `Không tìm thấy thư mục "${SOURCE_NAME}" trong folder gốc. Kiểm tra tên hoặc chọn lại folder gốc.`
          );
          setLoading(false);
          return;
        }

        const perSource = await Promise.all(
          sources.map(async (src) =>
            (await listSubfolders(src.id, token)).map((f) => ({
              ...f,
              sourceId: src.id,
              fromLegacy: src.name.trim() !== SOURCE_NAME,
            }))
          )
        );
        const initial: UnsortedItem[] = perSource.flat().map((f) => ({
          id: f.id,
          name: f.name,
          sourceId: f.sourceId,
          fromLegacy: f.fromLegacy,
          renameTo: f.name,
          targetId: null,
          thumbnail: null,
          thumbLoaded: false,
          status: "idle",
        }));
        setItems(initial);

        // Fire-and-forget thumbnail fetches (parallel, non-blocking)
        initial.forEach((item) => {
          firstImageThumbnail(item.id, token)
            .then((thumb) => {
              setItems((prev) =>
                prev
                  ? prev.map((it) =>
                      it.id === item.id
                        ? { ...it, thumbnail: thumb, thumbLoaded: true }
                        : it
                    )
                  : prev
              );
            })
            .catch(() => {
              setItems((prev) =>
                prev
                  ? prev.map((it) =>
                      it.id === item.id ? { ...it, thumbLoaded: true } : it
                    )
                  : prev
              );
            });
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi tải dữ liệu");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Auto-load when parent is configured and we have a token.
  useEffect(() => {
    if (!parentId) return;
    void (async () => {
      const token = await ensureToken();
      if (!token) return;
      await loadFromParent(token, parentId, false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  // Esc closes the modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleConfigureParent = useCallback(() => {
    const id = extractFolderIdFromUrl(parentInput) ?? parentInput.trim();
    if (!id) {
      setError("Link folder không hợp lệ");
      return;
    }
    setSortParent(id);
    clearSortTargets();
    setTargets(null);
    setParentIdState(id);
  }, [parentInput]);

  const handleRefresh = useCallback(async () => {
    if (!parentId) return;
    const token = await ensureToken();
    if (!token) return;
    clearSortTargets();
    setTargets(null);
    await loadFromParent(token, parentId, true);
  }, [parentId, ensureToken, loadFromParent]);

  const handleReconfigure = useCallback(() => {
    clearSortParent();
    setParentIdState(null);
    setTargets(null);
    setItems(null);
    setParentInput("");
  }, []);

  const updateItem = useCallback(
    (id: string, patch: Partial<UnsortedItem>) => {
      setItems((prev) => (prev ? prev.map((it) => (it.id === id ? { ...it, ...patch } : it)) : prev));
    },
    []
  );

  const handleTrashOne = useCallback(
    async (item: UnsortedItem) => {
      const token = await ensureToken();
      if (!token) return;
      updateItem(item.id, { status: "deleting", error: undefined });
      try {
        await trashFolder(item.id, token);
        updateItem(item.id, { status: "deleted" });
      } catch (e) {
        updateItem(item.id, {
          status: "error",
          error: e instanceof Error ? e.message : "Lỗi xoá folder",
        });
      }
    },
    [ensureToken, updateItem]
  );

  const handleMoveOne = useCallback(
    async (item: UnsortedItem) => {
      if (!item.targetId) return;
      const token = await ensureToken();
      if (!token) return;

      const target = targets?.find((t) => t.id === item.targetId);
      if (!target) return;

      updateItem(item.id, { status: "moving", error: undefined });
      try {
        const newName = item.renameTo.trim() || item.name;
        // Tier from the target category the admin picked (same rule the
        // server used for target.suggestedTag), else the set name;
        // colour/season/scene/Indonesia etc. from the set name.
        const tags = suggestFolderTags(newName, target.name);
        await moveFolder(
          item.id,
          {
            newParentId: item.targetId,
            oldParentId: item.sourceId,
            ...(newName !== item.name ? { newName } : {}),
          },
          token
        );
        await bulkImport(
          [
            {
              folderId: item.id,
              folderName: newName,
              sortCategory: target.name,
              // Canonical kimono_tag values ("furisode", "lace", …) — never
              // the raw Drive folder name. Names no rule matches stay
              // untagged; Auto Tag's AI can fill them later.
              kimonoTags: tags.kimonoTags,
              shootTags: tags.shootTags,
              seasonTags: tags.seasonTags,
              sceneTags: tags.sceneTags,
              colorTags: tags.colorTags,
            },
          ],
          { notifyAs: "sort" },
        );
        updateItem(item.id, { status: "done" });
      } catch (e) {
        updateItem(item.id, {
          status: "error",
          error: e instanceof Error ? e.message : "Lỗi di chuyển",
        });
      }
    },
    [targets, ensureToken, updateItem]
  );

  // ── Render ───────────────────────────────────────────────────────────────
  const remaining = items?.filter(
    (it) => it.status !== "done" && it.status !== "deleted"
  ) ?? [];
  const legacyCount = remaining.filter((it) => it.fromLegacy).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 md:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        // Wide on laptops: many folders → more of them on screen at once.
        className="w-full max-w-[1600px] h-[94vh] flex flex-col rounded-2xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (sticky — always visible while the card grid scrolls) */}
        <div className="flex-shrink-0 border-b border-gray-100 bg-white px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Sắp xếp bộ ảnh mới tải xuống</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Quét folder <strong>{SOURCE_NAME}</strong>, chọn loại kimono cho từng bộ →
              <strong> di chuyển folder</strong> sang đúng nhóm trong Drive +
              tạo record trên Airtable. Dùng cho ảnh vừa download xong, chưa được sắp xếp.
              <br />
              <span className="text-gray-400">
                Folder chỉ ở yên tại chỗ? Dùng <strong>Thêm bộ ảnh</strong> bên cạnh.
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {parentId && (
              <>
                <button
                  onClick={() => void handleRefresh()}
                  disabled={loading}
                  className="text-xs text-gray-600 hover:text-gray-900 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 disabled:opacity-40"
                  title="Tải lại danh sách target + items"
                >
                  ↻ Refresh
                </button>
                <button
                  onClick={handleReconfigure}
                  className="text-xs text-gray-500 hover:text-gray-800 rounded-lg px-2.5 py-1.5 hover:bg-gray-100"
                  title="Đổi folder gốc"
                >
                  ⚙ Cài lại
                </button>
              </>
            )}
            <button
              onClick={onClose}
              aria-label="Đóng"
              title="Đóng (Esc)"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

        {/* Configure parent */}
        {!parentId && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Paste link folder <strong>01. Kimono</strong> trên Drive. App sẽ tự tìm subfolder{" "}
              <strong>{SOURCE_NAME}</strong> làm nguồn và liệt kê các folder loại kimono làm đích.
            </p>
            <input
              type="url"
              value={parentInput}
              onChange={(e) => setParentInput(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#B76E6E] focus:outline-none focus:ring-1 focus:ring-[#B76E6E]"
            />
            <button
              onClick={handleConfigureParent}
              disabled={!parentInput.trim()}
              className="w-full rounded-xl bg-[#B76E6E] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-40"
            >
              Dùng folder này
            </button>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Loading state */}
        {parentId && loading && !items && (
          <div className="py-10 text-center text-sm text-gray-500">Đang tải danh sách…</div>
        )}

        {/* Empty */}
        {parentId && !loading && items && items.length === 0 && (
          <div className="py-10 text-center text-sm text-gray-500">
            Không có folder nào trong <strong>{SOURCE_NAME}</strong>. 🎉
          </div>
        )}

        {/* Cards */}
        {parentId && items && items.length > 0 && targets && (
          <>
            <div className="mb-3 text-xs text-gray-500">
              {remaining.length} / {items.length} folder chờ phân loại
              {legacyCount > 0 && (
                <span className="ml-2 text-amber-700">
                  · {legacyCount} bộ còn ở <strong>{LEGACY_SOURCE_NAMES[0]}</strong> — kéo hết sang{" "}
                  <strong>{SOURCE_NAME}</strong> trên Drive rồi xoá folder cũ.
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
              {items.map((item) => (
                <Card
                  key={item.id}
                  item={item}
                  // Filter at render too: cached target lists predate inbox exclusions.
                  targets={targets.filter((t) => !EXCLUDED_TARGET_NAMES.has(t.name.trim()))}
                  onRename={(name) => updateItem(item.id, { renameTo: name })}
                  onPickTarget={(targetId) => updateItem(item.id, { targetId })}
                  onSubmit={() => void handleMoveOne(item)}
                  onTrash={() => void handleTrashOne(item)}
                />
              ))}
            </div>
          </>
        )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────

function Card({
  item,
  targets,
  onRename,
  onPickTarget,
  onSubmit,
  onTrash,
}: {
  item: UnsortedItem;
  targets: SortTarget[];
  onRename: (name: string) => void;
  onPickTarget: (targetId: string) => void;
  onSubmit: () => void;
  onTrash: () => void;
}) {
  const busy = item.status === "moving" || item.status === "deleting";
  const terminal = item.status === "done" || item.status === "deleted";
  const disabled = busy || terminal;
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col relative ${
        terminal ? "opacity-60" : ""
      }`}
    >
      {/* Top-right action overlay: Open Drive + Trash */}
      {!terminal && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
          <a
            href={folderUrl(item.id)}
            target="_blank"
            rel="noopener noreferrer"
            title="Mở folder trên Google Drive (xem toàn bộ ảnh)"
            aria-label="Mở folder trên Drive"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow hover:bg-blue-50 hover:text-blue-600 transition-colors"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
          <button
            onClick={onTrash}
            disabled={busy}
            title="Xoá folder (vào thùng rác Drive)"
            aria-label="Xoá folder"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow hover:bg-red-50 hover:text-red-600 disabled:opacity-40 transition-colors"
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>
      )}

      {/* Thumbnail */}
      <div className="h-36 xl:h-40 overflow-hidden bg-gray-100 relative">
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.name}
            className="h-full w-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs text-gray-400">
            {item.thumbLoaded ? "Không có ảnh" : "Đang tải…"}
          </div>
        )}
        {item.status === "done" && (
          <div className="absolute inset-0 flex items-center justify-center bg-green-500/70 text-white text-sm font-medium">
            ✓ Đã chuyển
          </div>
        )}
        {item.status === "deleted" && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-700/70 text-white text-sm font-medium">
            🗑 Đã xoá
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="text-[11px] text-gray-400 truncate" title={item.name}>
          {item.name}
        </div>

        <input
          type="text"
          value={item.renameTo}
          onChange={(e) => onRename(e.target.value)}
          disabled={disabled}
          placeholder="Tên mới"
          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-[#B76E6E] focus:outline-none focus:ring-1 focus:ring-[#B76E6E] disabled:bg-gray-50 disabled:text-gray-500"
        />

        <select
          value={item.targetId ?? ""}
          onChange={(e) => onPickTarget(e.target.value)}
          disabled={disabled}
          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-[#B76E6E] focus:outline-none focus:ring-1 focus:ring-[#B76E6E] disabled:bg-gray-50 disabled:text-gray-500"
        >
          <option value="">— Chọn loại kimono —</option>
          {targets.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.suggestedTag ? ` → ${t.suggestedTag}` : " → (chưa có tag)"}
            </option>
          ))}
        </select>

        {item.error && (
          <p className="text-xs text-red-600 line-clamp-2">{item.error}</p>
        )}

        <button
          onClick={onSubmit}
          disabled={disabled || !item.targetId}
          className="w-full rounded-lg bg-[#B76E6E] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-30 mt-auto"
        >
          {item.status === "moving"
            ? "Đang chuyển…"
            : item.status === "deleting"
            ? "Đang xoá…"
            : item.status === "done"
            ? "Xong"
            : item.status === "deleted"
            ? "Đã xoá"
            : item.status === "error"
            ? "Thử lại"
            : "Chuyển & Import"}
        </button>
      </div>
    </div>
  );
}
